import React, { useState, useEffect, useRef } from "react";
import { RefreshIcon, ChevronDownIcon, TrashIcon, CloseIcon, CheckIcon, SearchIcon } from "../SvgIcon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  modal,
  confirm,
  showSpinner,
  closeSpinner,
} from "../../modules/Dialogs";
import {
  createDiscount,
  updateDiscount,
  bulkDeleteDiscounts,
} from "../../modules/Discount";
import { getUsersClients } from "../../modules/Admin";
import { getCampaignList } from "../../modules/Campaign";
import CreateCampaignModal from "./CreateCampaignModal";

const CreateDiscount = ({
  view,
  discount,
  onClose,
  userDetails,
  theCallBackFunc,
}) => {
  const isEdit = view === "edit";
  const [isLoading, setIsLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [minJobValueRequired, setMinJobValueRequired] = useState(false);
  const [minJobValue, setMinJobValue] = useState("");
  const [appliesTo, setAppliesTo] = useState("allJobs");
  const [checkoutBehaviour, setCheckoutBehaviour] = useState("manual");
  const [specificJobsExpanded, setSpecificJobsExpanded] = useState(false);
  const [specificJobs, setSpecificJobs] = useState({
    sendItems: false,
    houseMove: false,
    disposalOfItems: false,
    wasteRemoval: false,
    houseClearance: false,
  });
  const [usageLimitsExpanded, setUsageLimitsExpanded] = useState(false);
  const [totalUsageLimit, setTotalUsageLimit] = useState("");
  const [endDateExpanded, setEndDateExpanded] = useState(false);
  const [customerEligibilityOption, setCustomerEligibilityOption] =
    useState("everyone");
  const [limitPerCustomer, setLimitPerCustomer] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState("");
  const [discountType, setDiscountType] = useState("everyone");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignsHasMore, setCampaignsHasMore] = useState(true);
  const campaignPageSize = 10;
  const campaignDropdownRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [issuedTo, setIssuedTo] = useState("");
  const [errors, setErrors] = useState({});
  const [campaignDropdownPosition, setCampaignDropdownPosition] = useState("bottom");

  // Client search state for userSpecific dropdown
  const [clientList, setClientList] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientPage, setClientPage] = useState(1);
  const [hasMoreClients, setHasMoreClients] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const clientPageSize = 50;
  const searchTimerRef = useRef(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateDiscountCode = (code) => {
    if (!code.trim()) return "";

    // Check if code exceeds 15 characters
    if (code.length > 15) {
      return "Discount code must not exceed 15 characters.";
    }

    // Check if code contains only alphanumeric characters or hyphens
    const codeRegex = /^[a-zA-Z0-9-]+$/;
    if (!codeRegex.test(code)) {
      return "Discount code must contain only letters, numbers, and hyphens.";
    }

    return "";
  };

  const clearAllErrors = () => setErrors({});

  const generateRandomCode = () => {
    let generatedCode;

    if (discountType === "userSpecific" && selectedUser) {
      const parts = selectedUser.split(" ");
      const firstName = parts[0] || "USER";
      const firstNameShort = firstName.trim().slice(0, 3);
      const shortIdMatch = selectedUser.match(/\(([^)]+)\)$/);
      const shortId = shortIdMatch ? shortIdMatch[1] : "";
      const shortIdPart = shortId.length >= 2 ? shortId.slice(-2) : shortId;
      const randomStr = Math.random()
        .toString(36)
        .substring(2, 6)
        .replace(/[^a-zA-Z0-9]/g, "")
        .padEnd(4, "0");
      generatedCode = `${firstNameShort}-${shortIdPart}${randomStr}`;
    } else {
      const randomStr = Math.random()
        .toString(36)
        .substring(2, 10)
        .replace(/[^a-zA-Z0-9]/g, "");
      generatedCode = randomStr;
    }

    // Ensure code doesn't exceed 15 characters
    if (generatedCode.length > 15) {
      generatedCode = generatedCode.substring(0, 15);
    }

    setDiscountCode(generatedCode);
    clearError("discountCode");
  };

  // Fetch clients from API
  const fetchClients = (search = clientSearch, page = 1, append = false) => {
    setClientsLoading(true);
    const queryParams = {
      filter: "active",
      search: search || "",
      page,
      limit: clientPageSize,
      sort: "",
    };
    getUsersClients(
      queryParams,
      (resStatus, responseString, resJson) => {
        setClientsLoading(false);
        if (resStatus == 200 || resStatus == "200") {
          const newData = resJson?.data || [];
          setClientList((prev) => (append ? [...prev, ...newData] : newData));
          setHasMoreClients(newData.length === clientPageSize);
          setClientPage(page);
        } else {
          setClientList((prev) => (append ? prev : []));
          setHasMoreClients(false);
        }
      },
      userDetails?.jwttoken,
    );
  };

  // Load clients and set checkout behaviour when switching to userSpecific tab
  useEffect(() => {
    if (discountType === "userSpecific") {
      fetchClients("", 1);
      setClientSearch("");
      setCheckoutBehaviour("automatic");
    }
  }, [discountType]);

  // Debounced client search
  const handleClientSearchChange = (e) => {
    const val = e.target.value;
    setClientSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchClients(val, 1);
    }, 400);
  };

  const handleClientPageChange = (newPage) => {
    fetchClients(clientSearch, newPage);
  };

  const handleClientScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Trigger the next page a bit earlier (200px from bottom) to handle fractional pixels easily
    if (Math.ceil(scrollTop + clientHeight) >= scrollHeight - 200) {
      if (!clientsLoading && hasMoreClients) {
        fetchClients(clientSearch, clientPage + 1, true);
      }
    }
  };

  const handleClientSelect = (client) => {
    const fullName =
      [client.accountInfo?.firstname, client.accountInfo?.lastname]
        .filter(Boolean)
        .join(" ") || "Unknown";
    const label = `${fullName} (${client.shortId || ""})`;
    setSelectedUser(label);
    setIssuedTo(client._id);

    const firstName = client.accountInfo?.firstname || "USER";
    const firstNameShort = firstName.trim().slice(0, 3);
    const shortIdPart =
      client.shortId && client.shortId.length >= 2
        ? client.shortId.slice(-2)
        : client.shortId || "";
    const randomStr = Math.random()
      .toString(36)
      .substring(2, 6)
      .replace(/[^a-zA-Z0-9]/g, "")
      .padEnd(4, "0");
    let generatedCode = `${firstNameShort}-${shortIdPart}${randomStr}`;

    // Ensure code doesn't exceed 15 characters
    if (generatedCode?.length > 15) {
      generatedCode = generatedCode.substring(0, 15);
    }

    setDiscountCode(generatedCode);
    clearError("discountCode");

    clearError("selectedUser");
    setClientDropdownOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setClientDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const fetchCampaigns = (page = 1, append = false) => {
    setCampaignsLoading(true);
    const queryParams = {
      filter: "",
      search: "",
      page,
      limit: campaignPageSize,
      sort: "",
    };
    getCampaignList(
      queryParams,
      (resStatus, responseString, resJson) => {
        setCampaignsLoading(false);
        if (resStatus == 200 || resStatus == "200") {
          const campaignList = resJson?.data || [];
          setCampaigns((prev) =>
            append ? [...prev, ...campaignList] : campaignList,
          );
          setCampaignsHasMore(campaignList.length === campaignPageSize);
          setCampaignPage(page);
        } else {
          setCampaigns((prev) => (append ? prev : []));
          setCampaignsHasMore(false);
        }
      },
      userDetails?.jwttoken,
    );
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(e.target)
      ) {
        setCampaignDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleCampaignScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (Math.ceil(scrollTop + clientHeight) >= scrollHeight - 200) {
      if (!campaignsLoading && campaignsHasMore) {
        fetchCampaigns(campaignPage + 1, true);
      }
    }
  };

  useEffect(() => {
    if (campaignDropdownOpen) {
      if (campaigns.length === 0) {
        setCampaignPage(1);
        setCampaignsHasMore(true);
        fetchCampaigns(1);
      }
      // Calculate dropdown position
      if (campaignDropdownRef.current) {
        setTimeout(() => {
          const triggerButton = campaignDropdownRef.current?.querySelector('.client-dropdown-trigger');
          if (triggerButton) {
            const rect = triggerButton.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 300; // approximate height of dropdown

            if (spaceBelow < dropdownHeight) {
              setCampaignDropdownPosition("top");
            } else {
              setCampaignDropdownPosition("bottom");
            }
          }
        }, 0);
      }
    }
  }, [campaignDropdownOpen]);

  useEffect(() => {
    if (isEdit && discount) {
      setDiscountCode(discount.code || discount.codes || "");
      let amountValue = "";
      if (discount.value !== undefined) amountValue = discount.value;
      else if (discount.amount !== undefined) amountValue = discount.amount;
      else if (discount.discountAmount !== undefined)
        amountValue = discount.discountAmount;

      if (amountValue !== "" && amountValue !== null) {
        setDiscountAmount(
          String(amountValue).includes("%") ? amountValue : `${amountValue}%`,
        );
      } else {
        setDiscountAmount("Select Discount");
      }
      setDiscountType(
        discount?.type === "marketing" || discount?.discountType === "everyone"
          ? "everyone"
          : "userSpecific",
      );
      setSelectedUser(discount?.selectedUser || "");
      setIssuedTo(discount?.issuedTo || "");

      const minJobValChecked =
        discount?.minimumJobValueChecked ||
        (discount?.minimumJobValue !== undefined &&
          discount?.minimumJobValue > 0);
      setMinJobValueRequired(!!minJobValChecked);
      setMinJobValue(discount?.minimumJobValue || "");

      setUsageLimitsExpanded(
        !!(discount?.totalUsageLimitChecked || discount?.totalUsageLimit),
      );
      setTotalUsageLimit(discount?.totalUsageLimit || "");

      setAppliesTo(discount?.appliesTo || "allJobs");
      setCheckoutBehaviour(discount?.checkoutBehaviour || "manual");
      setSpecificJobsExpanded(discount?.appliesTo === "specificJobs");

      if (discount?.customerEligibility) {
        setCustomerEligibilityOption(discount?.customerEligibility);
      }

      if (discount?.applicableJobType) {
        const newSpecificJobs = {
          sendItems: false,
          houseMove: false,
          disposalOfItems: false,
          wasteRemoval: false,
          houseClearance: false,
        };
        discount?.applicableJobType?.forEach((job) => {
          if (newSpecificJobs.hasOwnProperty(job)) {
            newSpecificJobs[job] = true;
          }
        });
        setSpecificJobs(newSpecificJobs);
      }

      setLimitPerCustomer(
        !!(
          discount?.perCustomerUsageLimitChecked ||
          discount?.perCustomerUsageLimit
        ),
      );

      setStartDate(
        discount?.startDateTime
          ? new Date(discount?.startDateTime)
          : discount?.startDate
            ? new Date(discount?.startDate)
            : new Date(),
      );

      setEndDateExpanded(!!discount?.endDateTimeChecked);
      if (discount?.endDateTimeChecked || discount?.endDate) {
        setEndDate(
          discount?.endDateTime
            ? new Date(discount?.endDateTime)
            : discount?.endDate
              ? new Date(discount?.endDate)
              : "",
        );
      } else {
        setEndDate("");
      }
    }
  }, [isEdit, discount]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    clearError("startDate");
    // If an endDate exists and is now less than or equal to the new startDate, clear it
    if (endDate && new Date(endDate) <= new Date(date)) {
      setEndDate("");
      if (!endDateExpanded) {
        setEndDateExpanded(false);
      }
    }
  };

  // Toggle specific job selection
  const handleSpecificJobToggle = (job) => {
    setSpecificJobs((prev) => ({
      ...prev,
      [job]: !prev[job],
    }));
    clearError("specificJobs");
  };

  const handleAppliesToChange = (option) => {
    if (option === "allJobs") {
      setAppliesTo("allJobs");
      setSpecificJobsExpanded(false);
    } else {
      setAppliesTo("specificJobs");
      setSpecificJobsExpanded(true);
    }
  };

  const handleCustomerEligibilityToggle = (option) => {
    setCustomerEligibilityOption(option);
  };

  const handleLimitPerCustomerToggle = () => {
    setLimitPerCustomer((prev) => !prev);
  };

  const handleDelete = () => {
    confirm(
      "Delete Discount",
      "Are you sure you want to delete this discount?",
      "Cancel",
      "Delete",
      () => {
        setIsLoading(true);
        showSpinner();

        bulkDeleteDiscounts(
          { couponIds: [discount?._id || discount?.id] },
          (resStatus, responseString, resJson) => {
            closeSpinner();
            setIsLoading(false);
            if (resStatus == 200 || resStatus == "200") {
              modal(
                resJson?.message ||
                  responseString ||
                  "Discount deleted successfully!",
              );
              onClose();
              if (theCallBackFunc) theCallBackFunc();
            } else {
              modal(
                resJson?.message ||
                  responseString ||
                  "Failed to delete discount.",
              );
            }
          },
          userDetails?.jwttoken,
        );
      },
    );
  };

  const handleSave = () => {
    let newErrors = {};

    if (discountType === "userSpecific" && !selectedUser) {
      newErrors.selectedUser = "Please select a user.";
    }
    if (!discountCode.trim()) {
      newErrors.discountCode = "Please enter a discount code.";
    } else {
      const codeValidationError = validateDiscountCode(discountCode);
      if (codeValidationError) {
        newErrors.discountCode = codeValidationError;
      }
    }
    if (!discountAmount || discountAmount === "Select Discount") {
      newErrors.discountAmount = "Please select or enter a discount amount.";
    }
    if (minJobValueRequired && (!minJobValue || Number(minJobValue) <= 0)) {
      newErrors.minJobValue = "Please enter a valid minimum job value.";
    }
    if (
      usageLimitsExpanded &&
      (!totalUsageLimit || Number(totalUsageLimit) <= 0)
    ) {
      newErrors.totalUsageLimit = "Please enter a valid total usage limit.";
    }
    if (!startDate) {
      newErrors.startDate = "Please select a start date.";
    }
    if (endDateExpanded && !endDate) {
      newErrors.endDate = "Please select an end date.";
    }
    if (
      endDateExpanded &&
      endDate &&
      new Date(endDate) <= new Date(startDate)
    ) {
      newErrors.endDate = "End date must be after start date.";
    }

    if (
      appliesTo === "specificJobs" &&
      !Object.values(specificJobs).some((val) => val === true)
    ) {
      newErrors.specificJobs = "Please select at least one specific job.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const payload = {
      code: discountCode,
      type: discountType === "userSpecific" ? "userSpecific" : "marketing",
      amount: parseInt(discountAmount) || 0,
      minimumJobValueChecked: minJobValueRequired,
      minimumJobValue: minJobValue ? Number(minJobValue) : 0,
      appliesTo: appliesTo,
      checkoutBehaviour: checkoutBehaviour,
      customerEligibility: customerEligibilityOption,
      totalUsageLimitChecked: usageLimitsExpanded,
      totalUsageLimit: totalUsageLimit ? Number(totalUsageLimit) : 0,
      perCustomerUsageLimitChecked: limitPerCustomer,
      perCustomerUsageLimit: limitPerCustomer ? 1 : null,
      startDateTime: startDate.toISOString(),
      endDateTimeChecked: endDateExpanded,
      endDateTime: endDateExpanded && endDate ? endDate.toISOString() : null,
      ...(selectedCampaign && { campaign: selectedCampaign }),
      ...(discountType === "userSpecific" && { selectedUser, issuedTo }),
      ...(appliesTo === "specificJobs" && {
        applicableJobType: Object.keys(specificJobs).filter(
          (k) => specificJobs[k],
        ),
      }),
    };

    if (isEdit) {
      setIsLoading(true);
      showSpinner();
      const updatePayload = {
        ...payload,
        id: discount?._id || discount?.id,
      };
      updateDiscount(
        updatePayload,
        (resStatus, responseString, resJson) => {
          setIsLoading(false);
          closeSpinner();
          if (resStatus == 200 || resStatus == "200") {
            modal(
              resJson?.message ||
                responseString ||
                "Discount updated successfully!",
              null,
              () => {
                onClose();
                if (theCallBackFunc) theCallBackFunc();
              },
            );
          } else {
            modal(
              resJson?.message ||
                responseString ||
                "Failed to update discount.",
            );
          }
        },
        userDetails?.jwttoken,
      );
    } else {
      setIsLoading(true);
      showSpinner();
      createDiscount(
        payload,
        (resStatus, responseString, resJson) => {
          setIsLoading(false);
          closeSpinner();
          if (resStatus == 200 || resStatus == "200") {
            modal(
              resJson?.message ||
                responseString ||
                "Discount created successfully!",
              null,
              () => {
                onClose();
                if (theCallBackFunc) theCallBackFunc();
              },
            );
          } else {
            modal(
              resJson?.message ||
                responseString ||
                "Failed to create discount.",
            );
          }
        },
        userDetails?.jwttoken,
      );
    }
  };

  return (
    <>
      <div className="discount-modal-overlay"></div>
      <div className="right-modal discount-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>
              <strong>
                {isEdit ? "Edit Details" : "Create Discount Code"}
              </strong>
            </h2>
            <button className="modal-close-btn" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {discountType === "everyone" && (
          <div className="modal-body">
            <div className="form-group">
              <h3>
                <strong>Discount audience</strong>
              </h3>
              <div className="tab-buttons">
                <button
                  className={`tab-button ${discountType === "everyone" ? "active" : ""}`}
                  onClick={() => {
                    setDiscountType("everyone");
                    clearAllErrors();
                    setDiscountCode("");
                    setSelectedUser("");
                  }}
                >
                  General discount
                </button>
                <button
                  className={`tab-button ${discountType === "userSpecific" ? "active" : ""}`}
                  onClick={() => {
                    setDiscountType("userSpecific");
                    clearAllErrors();
                    setDiscountCode("");
                    setSelectedUser("");
                  }}
                >
                  Client-specific discount
                </button>
              </div>
              <p className="coupon-description">
                For codes any eligible Client can use if they know the code.
              </p>
            </div>
            <div className="modal-checkbox-block">
              <h3>
                <strong>Checkout behaviour</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="manual"
                  checked={checkoutBehaviour === "manual"}
                  onChange={() => setCheckoutBehaviour("manual")}
                />
                Manual code only
                <span className="checkbox"></span>
              </label>

              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="automatic"
                  checked={checkoutBehaviour === "automatic"}
                  onChange={() => setCheckoutBehaviour("automatic")}
                />
                Automatic discount
                <span className="checkbox"></span>
              </label>
              <p className="coupon-description">
                Hidden from Client accounts. Only works when entered manually.
              </p>
            </div>
            <div className="form-group modal-checkbox-block">
              <h5>
                <strong>Discount details</strong>
              </h5>
            </div>
            <div className="form-group">
              <label>
                <strong>Discount Code</strong>
              </label>
              <div className="discount-code-input">
                <input
                  type="text"
                  placeholder="e.g., SAVE10"
                  value={discountCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDiscountCode(value);
                    const validationError = validateDiscountCode(value);
                    if (validationError) {
                      setErrors((prev) => ({
                        ...prev,
                        discountCode: validationError,
                      }));
                    } else {
                      clearError("discountCode");
                    }
                  }}
                />
                {discountType === "userSpecific" && (
                  <button
                    type="button"
                    className="refresh-code-btn"
                    onClick={generateRandomCode}
                    title="Generate new code"
                  >
                    <RefreshIcon />
                  </button>
                )}
              </div>
              <p className="coupon-description">
                {discountType === "userSpecific"
                  ? "Once a user is selected, a code is auto-generated. You can edit it if needed."
                  : "Enter a unique alphanumeric code for this discount."}
              </p>
              {errors.discountCode && (
                <span className="discount-error-message">
                  {errors.discountCode}
                </span>
              )}
            </div>
            <div className="form-group">
              <label>
                <strong>Discount Amount</strong>
              </label>
              <select
                value={discountAmount}
                onChange={(e) => {
                  setDiscountAmount(e.target.value);
                  clearError("discountAmount");
                }}
              >
                <option>Select Discount</option>
                <option>5%</option>
                <option>10%</option>
                <option>15%</option>
                <option>20%</option>
              </select>
              {errors.discountAmount && (
                <span className="discount-error-message">
                  {errors.discountAmount}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="discount"
                  checked={minJobValueRequired}
                  onChange={() => setMinJobValueRequired((prev) => !prev)}
                />
                This discount requires a minimum job value
                <span htmlFor="discount" className="checkbox"></span>
              </label>

              {minJobValueRequired && (
                <div className="min-job-value">
                  <label>Minimum Job Value (£)</label>
                  <input
                    type="number"
                    placeholder=""
                    value={minJobValue}
                    onChange={(e) => {
                      setMinJobValue(e.target.value);
                      clearError("minJobValue");
                    }}
                  />
                  {errors.minJobValue && (
                    <span className="discount-error-message">
                      {errors.minJobValue}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="modal-checkbox-block">
              <h3>
                <strong> Applies To</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="allJobs"
                  checked={appliesTo === "allJobs"}
                  onChange={() => handleAppliesToChange("allJobs")}
                />
                <strong>All Jobs</strong>
                <span className="checkbox"></span>
              </label>

              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="specificJobs"
                  checked={appliesTo === "specificJobs"}
                  onChange={() => handleAppliesToChange("specificJobs")}
                />
                <strong> Specific Jobs</strong>
                <span className="checkbox"></span>
              </label>

              {specificJobsExpanded && (
                <div className="specific-jobs-block">
                  {[
                    { id: "senditems", label: "Send items" },
                    { id: "housemove", label: "House Move" },
                    { id: "disposalofitems", label: "Dispose of items" },
                    { id: "wasteremoval", label: "Waste removal" },
                    { id: "houseclearance", label: "House clearance" },
                  ].map((job) => (
                    <label key={job.id} className="custom-checkbox">
                      <input
                        type="checkbox"
                        id={job.id}
                        checked={specificJobs[job.id]}
                        onChange={() => handleSpecificJobToggle(job.id)}
                      />
                      {job.label}
                      <span className="checkbox"></span>
                    </label>
                  ))}
                  {errors.specificJobs && (
                    <span className="discount-error-message">
                      {errors.specificJobs}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Customer Eligibility</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="everyone"
                  name="checkbox"
                  checked={customerEligibilityOption === "everyone"}
                  onChange={() => handleCustomerEligibilityToggle("everyone")}
                />{" "}
                <strong>Everyone</strong>
                <span className="checkbox" htmlFor="everyone"></span>
              </label>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="new"
                  name="checkbox"
                  checked={customerEligibilityOption === "new"}
                  onChange={() => handleCustomerEligibilityToggle("new")}
                />
                <strong> New Customers Only</strong>
                <span className="checkbox" htmlFor="new"></span>
              </label>
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Usage Limits</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="limitTotal"
                  name="checkbox"
                  checked={usageLimitsExpanded}
                  onChange={() => setUsageLimitsExpanded(!usageLimitsExpanded)}
                />
                <strong>
                  {" "}
                  Limit number of times this discount can be used in total
                </strong>
                <span className="checkbox" htmlFor="limitTotal"></span>
              </label>
              {usageLimitsExpanded && (
                <div className="form-group">
                  <div className="min-job-value">
                    <label>
                      <strong>Total Usage Limit</strong>
                    </label>
                    <input
                      type="number"
                      placeholder=""
                      value={totalUsageLimit}
                      onChange={(e) => {
                        setTotalUsageLimit(e.target.value);
                        clearError("totalUsageLimit");
                      }}
                    />
                    {errors.totalUsageLimit && (
                      <span className="discount-error-message">
                        {errors.totalUsageLimit}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="limitPerCustomer"
                  name="checkbox"
                  checked={limitPerCustomer}
                  onChange={handleLimitPerCustomerToggle}
                />{" "}
                <strong>Limit to one time per customer</strong>
                <span className="checkbox" htmlFor="limitPerCustomer"></span>
              </label>
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Active Dates</strong>
              </h3>
              <div className="form-group">
                <label>
                  <strong>Start Date & Time</strong>
                </label>

                <DatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  timeInputLabel="Time:"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  showTimeInput
                />
                {errors.startDate && (
                  <span className="discount-error-message">
                    {errors.startDate}
                  </span>
                )}
              </div>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="setEndDate"
                  name="checkbox"
                  checked={endDateExpanded}
                  onChange={() => setEndDateExpanded(!endDateExpanded)}
                />{" "}
                <strong>Set End Date</strong>
                <span className="checkbox"></span>
              </label>
              {endDateExpanded && (
                <div>
                  <div className="form-group ml-32 mt-16">
                    <label>
                      <strong>End Date & Time</strong>
                    </label>
                    {
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                          setEndDate(date);
                          clearError("endDate");
                        }}
                        timeInputLabel="Time:"
                        dateFormat="MM/dd/yyyy h:mm aa"
                        showTimeInput
                        placeholderText="Pick a date"
                        minDate={startDate}
                      />
                    }
                    {errors.endDate && (
                      <span className="discount-error-message">
                        {errors.endDate}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Campaign</strong>
              </h3>
              <div className="form-group">
                <label>
                  <strong>Assign to campaign</strong>
                </label>
                <div
                  className="client-custom-dropdown"
                  ref={campaignDropdownRef}
                >
                  <button
                    type="button"
                    className={`client-dropdown-trigger ${campaignDropdownOpen ? "open" : ""}`}
                    onClick={() => setCampaignDropdownOpen((prev) => !prev)}
                  >
                    <span className="client-trigger-value">
                      {selectedCampaign
                        ? campaigns.find(
                            (c) => (c._id || c.id) === selectedCampaign,
                          )?.name || selectedCampaign
                        : "No campaign"}
                    </span>
                    <ChevronDownIcon className="client-trigger-chevron" />
                  </button>
                  {campaignDropdownOpen && (
                    <div className={`client-dropdown-panel campaign-dropdown-panel ${campaignDropdownPosition === "top" ? "dropdown-top" : "dropdown-bottom"}`}>
                      <div
                        className="client-dropdown-options"
                        onScroll={handleCampaignScroll}
                      >
                        <div
                          className="client-option-row campaign-option-row"
                          onClick={() => {
                            setSelectedCampaign("");
                            setCampaignDropdownOpen(false);
                          }}
                        >
                          {!selectedCampaign && (
                            <CheckIcon className="campaign-check-icon" />
                          )}
                          <span className="client-option-name">
                            No campaign
                          </span>
                        </div>
                        {campaigns?.length === 0 && !campaignsLoading && (
                          <div className="client-dropdown-empty">
                            No campaigns found.
                          </div>
                        )}
                        {campaigns?.map((campaign) => (
                          <div
                            key={campaign?._id || campaign?.id}
                            className="client-option-row campaign-option-row"
                            onClick={() => {
                              setSelectedCampaign(campaign._id || campaign.id);
                              setCampaignDropdownOpen(false);
                            }}
                          >
                            {selectedCampaign ===
                              (campaign?._id || campaign?.id) && (
                              <CheckIcon className="campaign-check-icon" />
                            )}
                            <span className="client-option-name">
                              {campaign?.name}
                            </span>
                          </div>
                        ))}
                        {campaigns?.length > 0 && campaignsLoading && (
                          <div className="client-dropdown-loading">
                            Loading more...
                          </div>
                        )}
                      </div>
                      <div
                        className="client-option-row campaign-create-new"
                        onClick={() => {
                          setCampaignDropdownOpen(false);
                          setShowCreateCampaignModal(true);
                        }}
                      >
                        <span>+ Create new campaign</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="coupon-description">
                  Optional internal context for why this code was created.
                </p>
              </div>
            </div>
          </div>
        )}

        {discountType === "userSpecific" && (
          <div className="modal-body">
            <div className="form-group">
                <h3>
                <strong>Discount audience</strong>
              </h3>
              <div className="tab-buttons">
                <button
                  className={`tab-button ${discountType === "everyone" ? "active" : ""}`}
                  onClick={() => {
                    setDiscountType("everyone");
                    clearAllErrors();
                    setDiscountCode("");
                    setSelectedUser("");
                  }}
                >
                  General discount
                </button>
                <button
                  className={`tab-button ${discountType === "userSpecific" ? "active" : ""}`}
                  onClick={() => {
                    setDiscountType("userSpecific");
                    clearAllErrors();
                    setDiscountCode("");
                    setSelectedUser("");
                  }}
                >
                  Client-specific discount
                </button>
              </div>
              <p className="coupon-description">
                For codes assigned to one Client. Always shown and auto-applied
                at checkout.
              </p>
            </div>
            <div className="modal-checkbox-block">
              <h3>
                <strong>Checkout behaviour</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="manual"
                  checked={checkoutBehaviour === "manual"}
                  onChange={() => setCheckoutBehaviour("manual")}
                  disabled
                />
                Manual code only
                <span className="checkbox"></span>
              </label>

              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="automatic"
                  checked={checkoutBehaviour === "automatic"}
                  onChange={() => setCheckoutBehaviour("automatic")}
                  disabled
                />
                Automatic discount
                <span className="checkbox"></span>
              </label>
              <p className="coupon-description">
                Client-specific discounts are always shown and auto-applied at checkout.
              </p>
            </div>
            <div className="form-group modal-checkbox-block">
               <h3>
                <strong>Discount details</strong>
              </h3>
              <label>
                <strong>Assigned User</strong>
              </label>
              {/* Custom dropdown wrapper */}
              <div className="client-custom-dropdown" ref={dropdownRef}>
                {/* Trigger button */}
                <button
                  type="button"
                  className={`client-dropdown-trigger ${clientDropdownOpen ? "open" : ""}`}
                  onClick={() => {
                    setClientDropdownOpen((prev) => !prev);
                    if (!clientDropdownOpen && clientList.length === 0) {
                      fetchClients("", 1);
                    }
                  }}
                >
                  <span
                    className={
                      selectedUser
                        ? "client-trigger-value"
                        : "client-trigger-placeholder"
                    }
                  >
                    {selectedUser || "Search by name, email, or ID"}
                  </span>
                  <ChevronDownIcon className="client-trigger-chevron" />
                </button>

                {/* Dropdown panel */}
                {clientDropdownOpen && (
                  <div className="client-dropdown-panel">
                    {/* Search */}
                    <div className="client-dropdown-search-wrap">
                      <SearchIcon width={14} height={14} stroke="#999" strokeWidth={2} />
                      <input
                        type="text"
                        className="client-dropdown-search"
                        placeholder="Type name, email, or ID..."
                        value={clientSearch}
                        onChange={handleClientSearchChange}
                        autoFocus
                      />
                    </div>

                    {/* Options list */}
                    <div
                      className="client-dropdown-options"
                      onScroll={handleClientScroll}
                    >
                      {clientList.length === 0 && !clientsLoading && (
                        <div className="client-dropdown-empty">
                          No clients found.
                        </div>
                      )}
                      {clientList.length === 0 && clientsLoading && (
                        <div className="client-dropdown-loading">
                          Loading...
                        </div>
                      )}
                      {clientList.map((client) => {
                        const fullName =
                          [
                            client.accountInfo?.firstname,
                            client.accountInfo?.lastname,
                          ]
                            .filter(Boolean)
                            .join(" ") || "Unknown";
                        const label = `${fullName} (${client.shortId || ""})`;
                        const isSelected = selectedUser === label;
                        return (
                          <div
                            key={client._id || client.shortId}
                            className={`client-option-row ${isSelected ? "selected" : ""}`}
                            onClick={() => handleClientSelect(client)}
                          >
                            <span className="client-option-name">
                              {fullName}
                            </span>
                            <span className="client-option-meta">
                              {client.accountInfo?.email || ""}
                              {client.shortId ? ` • ${client.shortId}` : ""}
                            </span>
                          </div>
                        );
                      })}
                      {clientList.length > 0 && clientsLoading && (
                        <div className="client-dropdown-loading">
                          Loading more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.selectedUser && (
                <span className="discount-error-message">
                  {errors.selectedUser}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <strong>Discount Code</strong>
              </label>
              <div className="discount-code-input">
                <input
                  type="text"
                  placeholder="e.g., SAVE10"
                  value={discountCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDiscountCode(value);
                    const validationError = validateDiscountCode(value);
                    if (validationError) {
                      setErrors((prev) => ({
                        ...prev,
                        discountCode: validationError,
                      }));
                    } else {
                      clearError("discountCode");
                    }
                  }}
                />
                <button
                  type="button"
                  className="refresh-code-btn"
                  onClick={generateRandomCode}
                  title="Generate new code"
                  disabled={discountType === "userSpecific" && !selectedUser}
                >
                  <RefreshIcon />
                </button>
              </div>
              <p className="coupon-description">
                {discountType === "userSpecific"
                  ? "Once a user is selected, a code is auto-generated. You can edit it if needed."
                  : "Enter a unique alphanumeric code for this discount."}
              </p>
              {errors.discountCode && (
                <span className="discount-error-message">
                  {errors.discountCode}
                </span>
              )}
            </div>

            <div className="form-group">
              <label>
                <strong>Discount Amount</strong>
              </label>
              <select
                value={discountAmount}
                placeholder="Select Discount"
                onChange={(e) => {
                  setDiscountAmount(e.target.value);
                  clearError("discountAmount");
                }}
              >
                <option>Select Discount</option>
                <option>5%</option>
                <option>10%</option>
                <option>15%</option>
                <option>20%</option>
              </select>
              {errors.discountAmount && (
                <span className="discount-error-message">
                  {errors.discountAmount}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="discount"
                  checked={minJobValueRequired}
                  onChange={() => setMinJobValueRequired((prev) => !prev)}
                />
                This discount requires a minimum job value
                <span htmlFor="discount" className="checkbox"></span>
              </label>

              {minJobValueRequired && (
                <div className="min-job-value">
                  <label>Minimum Job Value (£)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={minJobValue}
                    onChange={(e) => {
                      setMinJobValue(e.target.value);
                      clearError("minJobValue");
                    }}
                  />
                  {errors.minJobValue && (
                    <span className="discount-error-message">
                      {errors.minJobValue}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="modal-checkbox-block">
              <h3>
                <strong> Applies To</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="allJobs"
                  checked={appliesTo === "allJobs"}
                  onChange={() => handleAppliesToChange("allJobs")}
                />
                <strong>All Jobs</strong>
                <span className="checkbox"></span>
              </label>

              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="specificJobs"
                  checked={appliesTo === "specificJobs"}
                  onChange={() => handleAppliesToChange("specificJobs")}
                />
                <strong> Specific Jobs</strong>
                <span className="checkbox"></span>
              </label>

              {specificJobsExpanded && (
                <div className="specific-jobs-block">
                  {[
                    { id: "senditems", label: "Send items" },
                    { id: "housemove", label: "House Move" },
                    { id: "disposalofitems", label: "Dispose of items" },
                    { id: "wasteremoval", label: "Waste removal" },
                    { id: "houseclearance", label: "House clearance" },
                  ].map((job) => (
                    <label key={job.id} className="custom-checkbox">
                      <input
                        type="checkbox"
                        id={job.id}
                        checked={specificJobs[job.id]}
                        onChange={() => handleSpecificJobToggle(job.id)}
                      />
                      {job.label}
                      <span className="checkbox"></span>
                    </label>
                  ))}
                  {errors.specificJobs && (
                    <span className="discount-error-message">
                      {errors.specificJobs}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="modal-checkbox-block">
              <h3>
                <strong>Usage Limits</strong>
              </h3>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="limitTotal"
                  name="checkbox"
                  checked={usageLimitsExpanded}
                  onChange={() => setUsageLimitsExpanded(!usageLimitsExpanded)}
                />
                <strong>
                  {" "}
                  Limit number of times this discount can be used in total
                </strong>
                <span className="checkbox" htmlFor="limitTotal"></span>
              </label>
              {usageLimitsExpanded && (
                <div className="form-group">
                  <div className="min-job-value">
                    <label>
                      <strong>Total Usage Limit</strong>
                    </label>
                    <input
                      type="number"
                      placeholder=""
                      value={totalUsageLimit}
                      onChange={(e) => {
                        setTotalUsageLimit(e.target.value);
                        clearError("totalUsageLimit");
                      }}
                    />
                    {errors.totalUsageLimit && (
                      <span className="discount-error-message">
                        {errors.totalUsageLimit}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="limitPerCustomer"
                  name="checkbox"
                  checked={limitPerCustomer}
                  onChange={handleLimitPerCustomerToggle}
                />{" "}
                <strong>Limit to one time per customer</strong>
                <span className="checkbox" htmlFor="limitPerCustomer"></span>
              </label>
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Active Dates</strong>
              </h3>
              <div className="form-group">
                <label>
                  <strong>Start Date & Time</strong>
                </label>

                <DatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  timeInputLabel="Time:"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  showTimeInput
                />
                {errors.startDate && (
                  <span className="discount-error-message">
                    {errors.startDate}
                  </span>
                )}
              </div>
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  id="setEndDate"
                  name="checkbox"
                  checked={endDateExpanded}
                  onChange={() => setEndDateExpanded(!endDateExpanded)}
                />{" "}
                <strong>Set End Date</strong>
                <span className="checkbox"></span>
              </label>
              {endDateExpanded && (
                <div>
                  <div className="form-group ml-32 mt-16">
                    <label>
                      <strong>End Date & Time</strong>
                    </label>
                    {
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => {
                          setEndDate(date);
                          clearError("endDate");
                        }}
                        timeInputLabel="Time:"
                        dateFormat="MM/dd/yyyy h:mm aa"
                        showTimeInput
                        placeholderText="Pick a date"
                        minDate={startDate}
                      />
                    }
                    {errors.endDate && (
                      <span className="discount-error-message">
                        {errors.endDate}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-checkbox-block">
              <h3>
                <strong>Campaign</strong>
              </h3>
              <div className="form-group">
                <label>
                  <strong>Assign to campaign</strong>
                </label>
                <div
                  className="client-custom-dropdown"
                  ref={campaignDropdownRef}
                >
                  <button
                    type="button"
                    className={`client-dropdown-trigger ${campaignDropdownOpen ? "open" : ""}`}
                    onClick={() => setCampaignDropdownOpen((prev) => !prev)}
                  >
                    <span className="client-trigger-value">
                      {selectedCampaign
                        ? campaigns.find(
                            (c) => (c._id || c.id) === selectedCampaign,
                          )?.name || selectedCampaign
                        : "No campaign"}
                    </span>
                    <ChevronDownIcon className="client-trigger-chevron" />
                  </button>
                  {campaignDropdownOpen && (
                    <div className={`client-dropdown-panel campaign-dropdown-panel ${campaignDropdownPosition === "top" ? "dropdown-top" : "dropdown-bottom"}`}>
                      <div
                        className="client-dropdown-options"
                        onScroll={handleCampaignScroll}
                      >
                        <div
                          className="client-option-row campaign-option-row"
                          onClick={() => {
                            setSelectedCampaign("");
                            setCampaignDropdownOpen(false);
                          }}
                        >
                          {!selectedCampaign && (
                            <CheckIcon className="campaign-check-icon" />
                          )}
                          <span className="client-option-name">
                            No campaign
                          </span>
                        </div>
                        {campaigns?.length === 0 && !campaignsLoading && (
                          <div className="client-dropdown-empty">
                            No campaigns found.
                          </div>
                        )}
                        {campaigns?.map((campaign) => (
                          <div
                            key={campaign._id || campaign.id}
                            className="client-option-row campaign-option-row"
                            onClick={() => {
                              setSelectedCampaign(campaign?._id || campaign?.id);
                              setCampaignDropdownOpen(false);
                            }}
                          >
                            {selectedCampaign ===
                              (campaign?._id || campaign?.id) && (
                              <CheckIcon className="campaign-check-icon" />
                            )}
                            <span className="client-option-name">
                              {campaign?.name}
                            </span>
                          </div>
                        ))}
                        {campaigns?.length > 0 && campaignsLoading && (
                          <div className="client-dropdown-loading">
                            Loading more...
                          </div>
                        )}
                      </div>
                      <div
                        className="client-option-row campaign-create-new"
                        onClick={() => {
                          setCampaignDropdownOpen(false);
                          setShowCreateCampaignModal(true);
                        }}
                      >
                        <span>+ Create new campaign</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="coupon-description">
                  Optional internal context for why this code was created.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <div className="d-flex align-items-center g-12">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isEdit ? "Update Discount" : "Create Discount"}
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
          {isEdit && discount?.computedStatus?.toLowerCase() !== "revoked" && (
            <button
              className="btn btn-danger icon-with-btn"
              onClick={handleDelete}
              disabled={isLoading}
            >
              <TrashIcon />
              {isLoading ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>

        <CreateCampaignModal
          show={showCreateCampaignModal}
          onClose={() => setShowCreateCampaignModal(false)}
          onSuccess={(newCampaign) => {
            setCampaigns((prev) => [...prev, newCampaign]);
            setSelectedCampaign(newCampaign?._id || newCampaign?.id);
            setShowCreateCampaignModal(false);
            fetchCampaigns(1);
          }}
          userDetails={userDetails}
        />
      </div>
    </>
  );
};

export default CreateDiscount;
